import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';

// components
import LoginForm from '../sections/auth/login/LoginForm';
import useCommunityStats from '../hooks/useCommunityStats';

// ----------------------------------------------------------------------

export default function LoginPage() {
  const communityStats = useCommunityStats();
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Login | Mutual Trust Financial System</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'stretch',
          backgroundColor: 'background.default',
        }}
      >
        {/* Left Side: Brand Showcase Panel (Visible on lg/md screens) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: { md: '48%', lg: '45%' },
            position: 'relative',
            overflow: 'hidden',
            p: { md: 6, lg: 8 },
            color: '#FFFFFF',
            background: 'linear-gradient(145deg, #071C2E 0%, #0B3B60 55%, #054837 100%)',
            boxShadow: 'inset -1px 0 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Subtle Ambient Radial Glow */}
          <Box
            sx={{
              position: 'absolute',
              top: '-15%',
              left: '-15%',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(56, 189, 248, 0.18) 0%, rgba(11, 59, 96, 0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10%',
              right: '-10%',
              width: '450px',
              height: '450px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0) 70%)',
              pointerEvents: 'none',
            }}
          />

          {/* Top Branding Header */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Box
                component="img"
                src="/assets/icons/mtc.png"
                alt="Mutual Trust Logo"
                sx={{
                  width: 52,
                  height: 52,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.04em', lineHeight: 1.1 }}>
                  MUTUAL TRUST
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', letterSpacing: '0.08em', fontWeight: 600 }}>
                  COOPERATIVE MULTIPURPOSE SOCIETY
                </Typography>
              </Box>
            </Stack>

            <Chip
              label="✨ Established 2007 • 100% Non-Interest Framework"
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.75rem',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                mb: 3,
              }}
            />

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.18,
                letterSpacing: '-0.02em',
                mb: 2.5,
                color: '#FFFFFF',
              }}
            >
              Ethical Financial Stewardship & Member Prosperity
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.82)',
                lineHeight: 1.6,
                maxWidth: 480,
                fontSize: '1rem',
              }}
            >
              Secure, digital record keeping for community savings, ethical non-interest loans, member business partnerships, and cooperative shares.
            </Typography>
          </Box>

          {/* Key Pillars Highlights */}
          <Box sx={{ position: 'relative', zIndex: 1, my: 4 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 20, color: '#38BDF8' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                    Non-Interest Islamic Compliance
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Strict adherence to Qur'anic ethical commerce principles (Qur'an 2:278-279).
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <GroupsRoundedIcon sx={{ fontSize: 20, color: '#34D399' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                    {communityStats.displayText}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Academics, civil servants, artisans, and self-employed entrepreneurs.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <AccountBalanceRoundedIcon sx={{ fontSize: 20, color: '#FCD34D' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                    Jaiz Bank Operational Accounts
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Dedicated non-interest banking accounts for main, business, and welfare funds.
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Bottom Stats Footer */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              pt: 3,
              borderTop: '1px solid rgba(255, 255, 255, 0.15)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFFFFF' }}>
                  2007
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Founded
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#34D399' }}>
                  100%
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Non-Interest
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#38BDF8' }}>
                  9 Portfolios
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Managed Ledgers
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Right Side: Authentication Form Card */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: { xs: 3, sm: 6, md: 8 },
            maxWidth: { xs: '100%', md: '55%' },
            mx: 'auto',
          }}
        >
          {/* Top Bar with Navigation Link */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 4, md: 6 } }}>
            <Button
              component={RouterLink}
              to="/"
              variant="text"
              startIcon={<ArrowBackRoundedIcon />}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              Back to Home
            </Button>

            <Button
              component={RouterLink}
              to="/history"
              variant="outlined"
              size="small"
              startIcon={<HistoryRoundedIcon />}
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: '0.8rem',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              Our Story
            </Button>
          </Stack>

          {/* Main Card Content */}
          <Box sx={{ maxWidth: 440, width: '100%', mx: 'auto', my: 'auto' }}>
            {/* Mobile Logo Display */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box
                component="img"
                src="/assets/icons/mtc.png"
                alt="Mutual Trust Logo"
                sx={{ width: 44, height: 44, objectFit: 'contain' }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.1 }}>
                  MUTUAL TRUST
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Cooperative Multipurpose Society
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  letterSpacing: '-0.02em',
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                }}
              >
                Welcome back
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.925rem' }}>
                Sign in with your authorized credentials to access member portfolios and administrative ledgers.
              </Typography>
            </Box>

            {/* Login Form Component */}
            <LoginForm />

            {/* Footer Notice */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Need help accessing your cooperative account?
              </Typography>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Contact Secretariat: 08034660713 • Abeokuta, Ogun State
              </Typography>
            </Box>
          </Box>

          {/* Bottom Copyright */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              © 2007 — {new Date().getFullYear()} Mutual Trust Cooperative Multipurpose Society. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
